/* eslint-disable max-classes-per-file */
import React from 'react';

import { Collection, List, Map, OrderedMap, Record } from 'immutable';
import isEmpty from 'just-is-empty';
import moment from 'moment';
import stringHash from 'string-hash';

import { validateRequired } from 'shared/helpers/validations';
import { NurseSpecialtyOption } from 'shared/selectors';
import { NurseSpecialtySkillDescription } from 'shared/types';

const workExperienceParams: {
  id: number | null;
  companyName: string;
  location: string;
  jobTitle: string | null;
  startMonth: number | string | null;
  startYear: number | string | null;
  endMonth: number | string | null;
  endYear: number | string | null;
  currentlyThere: boolean;
  duties: string | null;
  workExperienceSpecialties: List<any>;
  _destroy: boolean;
} = {
  id: null,
  companyName: '',
  location: '',
  jobTitle: '',
  startMonth: '',
  startYear: '',
  endMonth: '',
  endYear: '',
  currentlyThere: false,
  duties: '',
  workExperienceSpecialties: List(),
  _destroy: false,
};

export class WorkExperience extends Record(workExperienceParams) {
  public readonly id!: number | null;

  public readonly companyName!: string;

  public readonly location!: string;

  public readonly jobTitle!: string | null;

  public readonly startMonth!: number | string | null;

  public readonly startYear!: number | string | null;

  public readonly endMonth!: number | string | null;

  public readonly endYear!: number | string | null;

  public readonly duties!: string | null;

  public readonly currentlyThere!: boolean;

  public readonly workExperienceSpecialties: List<any>;

  public readonly _destroy: boolean;

  isEmpty() {
    return (
      isEmpty(this.companyName) &&
      isEmpty(this.location) &&
      isEmpty(this.jobTitle) &&
      isEmpty(this.startMonth) &&
      isEmpty(this.startYear) &&
      isEmpty(this.endMonth) &&
      isEmpty(this.endYear) &&
      isEmpty(this.duties) &&
      this.getKeptSpecialties().isEmpty()
    );
  }

  getDate(type: 'start' | 'end') {
    let date = '';
    const month = this.get(`${type}Month`);
    const year = this.get(`${type}Year`);
    if (month && year) {
      date = `${year}-${month}-01`;
    }
    return date;
  }

  getStartDate() {
    return this.getDate('start');
  }

  getEndDate() {
    return this.currentlyThere ? '' : this.getDate('end');
  }

  getKeptSpecialties() {
    // OnboardingResume uses Immutable merge() which converts plain JS object
    // to Map when manually adding or removing specialties.
    // However, WorkExperienceControl treats this as a plain JS object.
    return (
      this.workExperienceSpecialties?.filter((wes) =>
        Map.isMap(wes) ? wes.get('_destroy') !== true : wes._destroy !== true
      ) || List()
    );
  }

  equals(other) {
    return (
      this.companyName === other.companyName &&
      this.location === other.location &&
      this.jobTitle === other.jobTitle &&
      this.startMonth === other.startMonth &&
      this.startYear === other.startYear &&
      this.endMonth === other.endMonth &&
      this.endYear === other.endYear &&
      this.currentlyThere === other.currentlyThere &&
      this.duties === other.duties &&
      this.workExperienceSpecialties.equals(other.workExperienceSpecialties)
    );
  }

  hashCode() {
    return stringHash(
      [
        this.id,
        this.companyName,
        this.location,
        this.jobTitle,
        this.startMonth,
        this.startYear,
        this.endMonth,
        this.endYear,
        this.currentlyThere,
        this.duties,
        this.workExperienceSpecialties,
      ].join(' - ')
    );
  }
}

export function parseServerWorkExperience(we) {
  const startMoment = moment(we.start_date);
  const endMoment = we.end_date ? moment(we.end_date) : null;

  return new WorkExperience({
    id: we.id,
    companyName: we.company_name,
    location: we.location,
    jobTitle: we.job_title,
    startMonth: startMoment.month() + 1,
    startYear: startMoment.year(),
    endMonth: endMoment ? endMoment.month() + 1 : '',
    endYear: endMoment ? endMoment.year() : '',
    currentlyThere: endMoment == null,
    duties: we.duties || '',
    workExperienceSpecialties: List(we.work_experience_specialties),
    _destroy: false,
  });
}

class WorkExperienceValidationResult extends Record({
  errors: Map(),
  validWorkExperiences: List(),
}) {
  public readonly errors!: Map<number, any>;

  public readonly validWorkExperiences!: List<any>;
}

export const validateWorkExperiences = function (
  workExperiences: List<WorkExperience>
) {
  let errors = Map<number, any>();
  let validWorkExperiences = List();
  workExperiences.forEach((we, i) => {
    if (!we.isEmpty()) {
      let curErrors = validateRequired(we, 'companyName', 'jobTitle');

      if (we.getStartDate() === '') {
        curErrors = curErrors.set('startDate', 'Required');
      }

      if (we.getEndDate() === '' && !we.currentlyThere) {
        curErrors = curErrors.set('endDate', 'Required');
      }

      if (we.location && we.location.indexOf(',') === -1) {
        curErrors = curErrors.set(
          'location',
          'Please separate city and state/country with a comma. For example: "Chicago, IL"'
        );
      }

      if (curErrors.size === 0) {
        validWorkExperiences = validWorkExperiences.push(we);
      } else {
        errors = errors.set(i, curErrors);
      }
    }
  });

  return new WorkExperienceValidationResult({
    validWorkExperiences,
    errors,
  });
};

export function WorkExperienceSpecialtySkillEntry({
  genOnChange,
  nurseSpecialtyOptions,
  nurseSpecialtySkills,
  selectedSkillIds,
  specialtyId,
}: {
  genOnChange: (val: number) => React.ChangeEventHandler<HTMLInputElement>;
  nurseSpecialtyOptions: OrderedMap<number, NurseSpecialtyOption>;
  nurseSpecialtySkills: Collection.Keyed<
    number,
    NurseSpecialtySkillDescription[]
  >;
  selectedSkillIds: [];
  specialtyId: number;
}) {
  const allSkillsForSpecialty = nurseSpecialtySkills.get(specialtyId);
  if (!allSkillsForSpecialty) {
    return null;
  }

  const specialtyName = nurseSpecialtyOptions.get(specialtyId).label;

  return (
    <>
      <div>
        <label className="specialty-skill-label mb-1 block text-label-sm font-bold">
          {specialtyName} Functions/Skills Performed
        </label>
      </div>
      {allSkillsForSpecialty.map((skill) => {
        const selected = selectedSkillIds.some((ssi) => ssi === skill.id);

        return (
          <div key={`specialty-${specialtyId}-skill-${skill.id}`}>
            <label
              className={`flex items-center gap-2 ${
                selected
                  ? 'specialty-skill-selected'
                  : 'specialty-skill-deselected'
              }`}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={genOnChange(skill.id)}
              />
              {skill.description}
            </label>
          </div>
        );
      })}
    </>
  );
}

export const newWorkExperienceSpecialty = (specialtyId) => ({
  nurse_specialty_description_id: specialtyId,
  work_experience_specialty_skills: [],
});

export const newWorkExperienceSpecialtySkill = (skillId) => ({
  nurse_specialty_skill_description_id: skillId,
});
